/**
 * Hold a local drag-reorder until the host catches up.
 *
 * The list components emit a `*-moved` event carrying the new order and let the
 * host persist it. The documented host pattern (see docs/notes.md) is to write
 * to the server and let the round-trip re-push the array — deliberately NOT to
 * push optimistically. That leaves a window where the host's own array still
 * holds the pre-drag order.
 *
 * Any unrelated interaction in that window — saving an edit, toggling a pin —
 * makes the host re-push that stale array, and the component would adopt it and
 * silently undo the drag the user just performed.
 *
 * This reconciles the two. It is deliberately narrow: it only overrides an
 * incoming order that is EXACTLY the snapshot we know is out of date. Anything
 * else is treated as a genuine change and the host wins, so a real server-side
 * reorder, an insert, or a delete is never fought.
 */

const sameSequence = (a, b) => a.length === b.length && a.every((x, i) => x === b[i]);

/**
 * @param {Array} next     the array the host just assigned
 * @param {?{before: string[], after: string[]}} pending  ids before/after the local drag
 * @param {(item: any) => string} idOf
 * @returns {{ list: Array, pending: ?object }} the list to store, and the
 *          pending reorder to keep holding (null once it is resolved)
 */
export function reconcileOrder(next, pending, idOf) {
  if (!pending || !Array.isArray(next) || !next.length) return { list: next, pending };

  const ids = next.map(idOf);

  // The host has caught up — its order is ours. Stop holding.
  if (sameSequence(ids, pending.after)) return { list: next, pending: null };

  // The host echoed back exactly the pre-drag order, so this push predates our
  // drag rather than disagreeing with it. Re-apply our order and keep holding
  // until the host acknowledges (or genuinely changes) it.
  if (sameSequence(ids, pending.before)) {
    const byId = new Map(next.map((item) => [idOf(item), item]));
    const reordered = pending.after.map((id) => byId.get(id)).filter(Boolean);
    if (reordered.length === next.length) return { list: reordered, pending };
  }

  // Anything else is a real change: membership differs, or the host reordered
  // for its own reasons. The host is authoritative.
  return { list: next, pending: null };
}

/**
 * The ids either side of `id` in `ids` — where a dropped item now sits.
 *
 * Indexes only make sense within the list the user saw, which is often a
 * slice of the collection (a kanban column, a filtered task list). Neighbours
 * don't: "right after B" places the item in the whole collection's order
 * without disturbing records the view never showed. `null` at either edge.
 *
 * @param {string[]} ids  the view's order after the drop
 * @param {string} id     the item that moved
 * @returns {{ afterId: ?string, beforeId: ?string }}
 */
export function neighbours(ids, id) {
  const i = ids.indexOf(id);
  if (i < 0) return { afterId: null, beforeId: null };
  return { afterId: i > 0 ? ids[i - 1] : null, beforeId: i < ids.length - 1 ? ids[i + 1] : null };
}
